const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/api/v1';

/* ------------------------------------------------------------------ */
/*  Helper: build URL with query-string params (skips empty values)   */
/* ------------------------------------------------------------------ */
function buildUrl(path, params = {}) {
  const url = new URL(`${API_BASE_URL}${path}`);
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.append(key, value);
    }
  });
  return url.toString();
}

/* ------------------------------------------------------------------ */
/*  Samples                                                           */
/* ------------------------------------------------------------------ */

export async function fetchSamples(params = {}) {
  try {
    const url = buildUrl('/rnd/samples', params);
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Failed to fetch samples: ${response.statusText}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to fetch samples');
    return data.data; // { samples, total, page, pageSize, totalPages }
  } catch (err) {
    console.error('fetchSamples error:', err);
    throw err;
  }
}

export async function createSample(body) {
  try {
    const response = await fetch(`${API_BASE_URL}/rnd/samples`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(`Failed to create sample: ${response.statusText}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to create sample');
    return data.data;
  } catch (err) {
    console.error('createSample error:', err);
    throw err;
  }
}

export async function fetchSampleById(id) {
  try {
    const response = await fetch(`${API_BASE_URL}/rnd/samples/${id}`);
    if (!response.ok) throw new Error(`Failed to fetch sample: ${response.statusText}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to fetch sample');
    return data.data; // { sample, statusHistory }
  } catch (err) {
    console.error('fetchSampleById error:', err);
    throw err;
  }
}

export async function updateSampleStatus(id, status, note) {
  try {
    const response = await fetch(`${API_BASE_URL}/rnd/samples/${id}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status, note }),
    });
    if (!response.ok) throw new Error(`Failed to update sample status: ${response.statusText}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to update sample status');
    return data.data;
  } catch (err) {
    console.error('updateSampleStatus error:', err);
    throw err;
  }
}

export async function updateSamplePriority(id, priority) {
  try {
    const response = await fetch(`${API_BASE_URL}/rnd/samples/${id}/priority`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ priority }),
    });
    if (!response.ok) throw new Error(`Failed to update sample priority: ${response.statusText}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to update sample priority');
    return data.data;
  } catch (err) {
    console.error('updateSamplePriority error:', err);
    throw err;
  }
}

export async function updateSample(id, fields) {
  try {
    const response = await fetch(`${API_BASE_URL}/rnd/samples/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(fields),
    });
    if (!response.ok) throw new Error(`Failed to update sample: ${response.statusText}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to update sample');
    return data.data;
  } catch (err) {
    console.error('updateSample error:', err);
    throw err;
  }
}

export async function exportSamplesCSV(params = {}) {
  try {
    const url = buildUrl('/rnd/samples/export', params);
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Failed to export samples: ${response.statusText}`);
    const blob = await response.blob();

    // Trigger browser download
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', 'rnd_samples_export.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);

    return blob;
  } catch (err) {
    console.error('exportSamplesCSV error:', err);
    throw err;
  }
}

/* ------------------------------------------------------------------ */
/*  Dashboard                                                         */
/* ------------------------------------------------------------------ */

export async function fetchOverview(params = {}) {
  try {
    const url = buildUrl('/rnd/dashboard/overview', params);
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Failed to fetch overview: ${response.statusText}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to fetch overview');
    return data.data;
  } catch (err) {
    console.error('fetchOverview error:', err);
    throw err;
  }
}

export async function fetchAgeing(params = {}) {
  try {
    const url = buildUrl('/rnd/dashboard/ageing', params);
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Failed to fetch ageing: ${response.statusText}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to fetch ageing');
    return data.data;
  } catch (err) {
    console.error('fetchAgeing error:', err);
    throw err;
  }
}

/* ------------------------------------------------------------------ */
/*  Masters                                                           */
/* ------------------------------------------------------------------ */

export async function fetchMasters() {
  try {
    const response = await fetch(`${API_BASE_URL}/rnd/masters`);
    if (!response.ok) throw new Error(`Failed to fetch masters: ${response.statusText}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to fetch masters');
    return data.data; // grouped by category
  } catch (err) {
    console.error('fetchMasters error:', err);
    throw err;
  }
}

export async function createMaster(body) {
  try {
    const response = await fetch(`${API_BASE_URL}/rnd/masters`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(`Failed to create master: ${response.statusText}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to create master');
    return data.data;
  } catch (err) {
    console.error('createMaster error:', err);
    throw err;
  }
}

export async function updateMaster(id, body) {
  try {
    const response = await fetch(`${API_BASE_URL}/rnd/masters/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(`Failed to update master: ${response.statusText}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to update master');
    return data.data;
  } catch (err) {
    console.error('updateMaster error:', err);
    throw err;
  }
}

export async function deleteMaster(id) {
  try {
    const response = await fetch(`${API_BASE_URL}/rnd/masters/${id}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) throw new Error(`Failed to delete master: ${response.statusText}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to delete master');
    return data.data;
  } catch (err) {
    console.error('deleteMaster error:', err);
    throw err;
  }
}

/* ------------------------------------------------------------------ */
/*  Ageing Bands                                                      */
/* ------------------------------------------------------------------ */

export async function fetchAgeingBands() {
  try {
    const response = await fetch(`${API_BASE_URL}/rnd/ageing-bands`);
    if (!response.ok) throw new Error(`Failed to fetch ageing bands: ${response.statusText}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to fetch ageing bands');
    return data.data;
  } catch (err) {
    console.error('fetchAgeingBands error:', err);
    throw err;
  }
}

export async function createAgeingBand(body) {
  try {
    const response = await fetch(`${API_BASE_URL}/rnd/ageing-bands`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(`Failed to create ageing band: ${response.statusText}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to create ageing band');
    return data.data;
  } catch (err) {
    console.error('createAgeingBand error:', err);
    throw err;
  }
}

export async function updateAgeingBand(id, body) {
  try {
    const response = await fetch(`${API_BASE_URL}/rnd/ageing-bands/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(`Failed to update ageing band: ${response.statusText}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to update ageing band');
    return data.data;
  } catch (err) {
    console.error('updateAgeingBand error:', err);
    throw err;
  }
}

export async function deleteAgeingBand(id) {
  try {
    const response = await fetch(`${API_BASE_URL}/rnd/ageing-bands/${id}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) throw new Error(`Failed to delete ageing band: ${response.statusText}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to delete ageing band');
    return data.data;
  } catch (err) {
    console.error('deleteAgeingBand error:', err);
    throw err;
  }
}
